import React from "react";

import Heading from "@common/components/Heading/Heading";
import Paragraph from "@common/components/Paragraph/Paragraph";
import { Spinner } from "@common/components/Spinner/Spinner";
import Tooltip from "@common/components/Tooltip/Tooltip";
import { TestingCoverageItem } from "@common/models/Analytics.model";
import { vars } from "@common/styles";
import { docLink, openDocsInNewTab } from "@common/util/docs";
import { quantity } from "@common/util/strings";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import useTestingCoverageData, { CoverageDimension } from "@main/pages/Release/consumers/useTestingCoverageData";
import { MetricCardEmptyState } from "@main/pages/Release/views/MetricCards/MetricCardEmptyState";
import * as MetricDetailsStyled from "main/pages/Release/views/MetricDetails/MetricDetails.styles";
import * as Styled from "main/pages/Release/views/MetricDetails/TestingCoverage/TestingCoverageDataView.styles";

export default function TestingCoverageDataView() {
  const { selectedApp } = useAppSelectionContext();
  const { dimensions, isLoading } = useTestingCoverageData();

  const isEmpty = dimensions.length === 0;

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <div style={{ padding: "3.2rem 0" }}>
      <MetricDetailsStyled.MetricsCard>
        <Heading
          heading3
          marginBottom={24}
        >
          Data
        </Heading>
        <Paragraph
          p1
          style={{ color: vars.colors.grey60, marginBottom: "1.2rem" }}
        >
          See how each enabled dimension was scored, and the details about every single category you defined.
        </Paragraph>
        <Styled.DimensionList>
          {!isEmpty ? (
            dimensions.map((dimension, index) => (
              <DimensionSection
                key={dimension.key}
                dimension={dimension}
                hasDivider={index > 0}
              />
            ))
          ) : (
            <MetricCardEmptyState
              text={"No sessions recorded yet for this release."}
              buttonText={"Learn more"}
              onButtonClick={() => openDocsInNewTab(docLink(selectedApp?.platform?.name).releases)}
              column
            />
          )}
        </Styled.DimensionList>
      </MetricDetailsStyled.MetricsCard>
    </div>
  );
}

interface DimensionSectionProps {
  dimension: CoverageDimension;
  hasDivider: boolean;
}

function DimensionSection({ dimension, hasDivider }: DimensionSectionProps) {
  return (
    <Styled.DimensionSection $hasDivider={hasDivider}>
      <Styled.DimensionScoreAndName>
        <Styled.DimensionScoreAndNameInner>
          <Tooltip
            style={{ width: "auto" }}
            text={dimension.scoreTooltip}
            position="top"
          >
            <Styled.DimensionScore
              style={{ marginRight: "1.6rem" }}
              score={dimension.score}
            />
          </Tooltip>
          <Styled.DimensionIconWrapper style={{ marginRight: "0.8rem" }}>{dimension.icon}</Styled.DimensionIconWrapper>
          <Styled.DimensionName p2>{dimension.label}</Styled.DimensionName>
        </Styled.DimensionScoreAndNameInner>
      </Styled.DimensionScoreAndName>
      <Styled.CategoryRows>
        {dimension.items.map((item) => (
          <CategoryRow
            key={item.value}
            item={item}
          />
        ))}
      </Styled.CategoryRows>
    </Styled.DimensionSection>
  );
}

interface CategoryRowProps {
  item: TestingCoverageItem;
}

function CategoryRow({ item }: CategoryRowProps) {
  let percentage: number | null = null;
  if (item?.minimum_sessions != null && item?.count != null) {
    percentage = (item.count / item.minimum_sessions) * 100;
    if (percentage > 100) percentage = 100;
  }

  return (
    <Styled.CategoryRow>
      <Styled.CategoryRowCell>
        <CategoryChip
          value={item.value}
          minimum={item.minimum_sessions}
        />
      </Styled.CategoryRowCell>

      <Styled.CategoryRowCell>
        <Tooltip
          text="Actual sessions"
          position="top"
        >
          <Styled.SessionsText
            style={{ textAlign: "center" }}
            p2
          >{`${item.count} ${quantity(item.count, "session", "sessions")}`}</Styled.SessionsText>
        </Tooltip>
      </Styled.CategoryRowCell>

      <Styled.CategoryRowCell style={{ justifyContent: "flex-end" }}>
        {percentage != null ? (
          <PercentageBar
            percentage={percentage}
            tooltip={"Category coverage"}
          />
        ) : null}
      </Styled.CategoryRowCell>
    </Styled.CategoryRow>
  );
}

interface CategoryChipProps {
  value: string;
  minimum?: number | null;
  image?: React.ReactNode;
}

function CategoryChip({ value, minimum, image }: CategoryChipProps) {
  const tooltipText =
    minimum == null
      ? "No minimum requirements set"
      : `Minimum ${minimum} ${quantity(minimum, "session", "sessions")} with ${value} required`;

  return (
    <Tooltip
      text={tooltipText}
      position="top"
    >
      <Styled.CategoryChip>
        {image}
        <Styled.CategoryChipValueCount>{value}</Styled.CategoryChipValueCount>
        {minimum != null && <Styled.CategoryChipMinimumCount p2>{minimum}</Styled.CategoryChipMinimumCount>}
      </Styled.CategoryChip>
    </Tooltip>
  );
}

interface PercentageBarProps {
  style?: React.CSSProperties;
  percentage: number;
  tooltip: string;
}

function PercentageBar({ style, percentage, tooltip }: PercentageBarProps) {
  return (
    <Tooltip
      style={{ width: "auto" }}
      text={tooltip}
      position="top"
    >
      <Styled.PercentageBar style={style}>
        <Styled.PercentageText p2>{`${percentage.toFixed(0)}%`}</Styled.PercentageText>

        <Styled.PercentageBarTrack>
          <Styled.PercentageBarFill $pct={percentage} />
        </Styled.PercentageBarTrack>
      </Styled.PercentageBar>
    </Tooltip>
  );
}
